import React, { useState, useEffect, useRef } from 'react';
import { useLazyQuery, useMutation } from '@apollo/client/react';
import moment from 'moment';
import ErrorMessage from '@components-clean/core/error';
import { RowMenuAction } from '@components-clean/core/grid/grid.interface';
import Loading from '@components-clean/core/loading/loading';
// eslint-disable-next-line max-len
import AddEditExcelListingsConfigDialog from '@pages-clean/risk-assessment/reporting/dialogs/add-edit-excel-listings-config/add-excel-listings-config-dialog';
import DeleteExcelListingsConfigDialog from '@pages-clean/risk-assessment/reporting/dialogs/delete-excel-listings-config-dialog';
import ExcelListingsGrid from '@pages-clean/risk-assessment/reporting/excel-listings/excelListings.grid';
import { Permissions } from 'contexts/user-context';
import { useTranslation, useBoolean, useAuthorization } from 'hooks';
import { GET_REPORT_DEFINITIONS } from 'queries/report-definition';
import { GENERATE_CUSTOM_REPORT } from 'queries/reports';
import { AuditApplicationArea, AuditDataType, AuditServiceType } from 'types/graphql-global-types';
import Constants from 'utils/constants';
import customReportGenerator from 'utils/custom-report-generator';
import { stringDateWithoutTimeFromUtc } from '@utils/convertor';
import NotificationService from '@utils/notification';
import { ADD_EXPORT_FE_GRID_DATA_AUDIT } from '@queries/audit';
import { ReportDefinitionFields, StudyFields } from '@queries/generated/fragments.generated';
import { getReportDefinitions } from '@queries/generated/report-definition.generated';
import {
    generateCustomReport,
    generateCustomReport_OpraQueries_generateCustomReport_CustomReport_tabs_CustomReportResultsTab_data_CustomReportResultsRowType as TabRow,
} from '@queries/generated/reports.generated';
import ControlsHubApi from '@apis/bff-api/central-monitoring/controls-hub';

const RISK_OUT_OF_TOLERANCE_COLUMN = 'RISK_OUT_OF_TOLERANCE';
const CM_CONTROLS_HUB_CHART_PREFIX = 'CM_CONTROLS_HUB_CHART';

interface RiskAssessmentExcelListingProps {
    study: StudyFields;
}

interface ReportDefinitionFieldsExtended extends ReportDefinitionFields {
    fullName?: string;
}

const RAMExcelListing = ({ study }: RiskAssessmentExcelListingProps) => {
    const studyId = study?.id || Constants.ObjectId.Empty;
    const user = useAuthorization();
    const [addExportFeGridDataAudit] = useMutation(ADD_EXPORT_FE_GRID_DATA_AUDIT);

    const { data: latestOOTValuesForPanel, isLoading } = ControlsHubApi.useGetLatestOOTValuesForPanel(study?.id, true);

    const { t } = useTranslation();
    const canViewInternalRisks = user?.can?.({
        module: Constants.Modules.RAM,
        element: Constants.DataElements.InternalRisks,
        permission: Permissions.Enabled,
    });
    const initialState = {
        runReport: false,
        runningFromMyReports: false,
    };
    const openAddReportConfigDialog = useBoolean(false);
    const openDeleteReportConfigDialog = useBoolean(false);
    const gridActioned = useRef<'User' | 'Organization' | undefined>(undefined);
    const addExcelListingConfigAction = useRef(false);
    const [state, setState] = useState({ ...initialState });
    const [currentReport, setCurrentReport] = useState<ReportDefinitionFieldsExtended | null>(null);
    const [myReports, setMyReports] = useState([] as ReportDefinitionFields[]);
    const [orgReports, setOrgReports] = useState([] as ReportDefinitionFields[]);
    const [loadReports, { data, error }] = useLazyQuery<getReportDefinitions>(GET_REPORT_DEFINITIONS);

    const canEditMyListings =
        study?.studyReadOnly !== true &&
        ((user?.canWriteSystemAdminLevel?.(false, false) || user?.isActiveOrgMember?.()) ?? false);

    const gridsWrapRef = useRef(null);

    useEffect(() => {
        if (study) loadReports().catch((err) => window.console.error(err));
    }, [study]);

    const clearState = () => {
        setState({ ...initialState });
    };

    const [loadCustomReport, { loading: loadingReport, data: customReportData }] = useLazyQuery(GENERATE_CUSTOM_REPORT);

    const applyRiskOutOfToleranceOverride = (reportData: generateCustomReport) => {
        reportData.generateCustomReport?.tabs?.forEach((tab) => {
            const riskOutOfToleranceIndex = tab?.columns?.findIndex(
                (column) => (column as unknown as string) === RISK_OUT_OF_TOLERANCE_COLUMN,
            );

            if (riskOutOfToleranceIndex == null || riskOutOfToleranceIndex < 0) return;

            tab?.data?.forEach((row) => {
                if (!row?.row) return;

                // The risk id is a mongo ObjectId string in the last field of the row.
                const riskId = row.row[row.row.length - 1];
                const associatedOOTValues = riskId
                    ? latestOOTValuesForPanel?.filter((panel) => panel.associatedRisks?.includes(riskId))
                    : [];

                if (!associatedOOTValues || associatedOOTValues.filter((panel) => panel.active).length === 0) {
                    row.row[riskOutOfToleranceIndex] = t('ram:riskLog.shared.notApplicable');
                } else if (
                    associatedOOTValues.some(
                        (panel) => panel?.status && (panel.status.includes('Red') || panel.status.includes('Amber')),
                    )
                ) {
                    row.row[riskOutOfToleranceIndex] = t('ram:riskLog.shared.yes');
                } else {
                    row.row[riskOutOfToleranceIndex] = t('ram:riskLog.shared.no');
                }
            });
        });

        return reportData;
    };

    const applyCMControlHubFields = (reportData: generateCustomReport) => {
        reportData.generateCustomReport?.tabs?.forEach((tab) => {
            if (!tab) return;

            const cmControlsHubChartColumns =
                tab.columns
                    ?.map((column, index) => ({ column, index }))
                    .filter(({ column }) => (column as unknown as string)?.startsWith(CM_CONTROLS_HUB_CHART_PREFIX)) ??
                [];

            if (cmControlsHubChartColumns.length === 0 || !tab.data) return;

            // The same risk id can already appear on multiple rows in this tab (e.g. one row per
            // associated control/action). Group those existing rows by risk id, then pair each one
            // with its own associatedOOTValues entry (by position) — no new rows are created here,
            // we only populate the CM_CONTROLS_HUB_CHART_* columns on the rows that already exist.
            const rowsByRiskId = new Map<string, TabRow[]>();
            tab.data.forEach((row) => {
                if (!row?.row) return;

                // The risk id is a mongo ObjectId string in the last field of the row.
                const riskId = row.row[row.row.length - 1];
                if (!riskId) return;

                const rowsForRiskId = rowsByRiskId.get(riskId) ?? [];
                rowsForRiskId.push(row);
                rowsByRiskId.set(riskId, rowsForRiskId);
            });

            rowsByRiskId.forEach((rowsForRiskId, riskId) => {
                const associatedOOTValues = latestOOTValuesForPanel?.filter((panel) =>
                    panel.associatedRisks?.includes(riskId),
                );
                if (!associatedOOTValues || associatedOOTValues.length === 0) return;

                rowsForRiskId.forEach((row, rowIndex) => {
                    const panel = associatedOOTValues[rowIndex];
                    if (!panel || !row.row) return;

                    cmControlsHubChartColumns.forEach(({ column, index }) => {
                        const columnKey = column as unknown as string;

                        if (columnKey === 'CM_CONTROLS_HUB_CHART_PLOT_TITLE') {
                            row.row![index] = panel.name ?? null;
                        }

                        if (columnKey === 'CM_CONTROLS_HUB_CHART_ASSOCIATED_RISKS') {
                            row.row![index] = String(panel.associatedRisks?.length ?? 0);
                        }

                        if (columnKey === 'CM_CONTROLS_HUB_CHART_DATASET') {
                            row.row![index] = stringDateWithoutTimeFromUtc(panel.dataSet, 'DD-MMM-YYYY') ?? null;
                        }

                        if (columnKey === 'CM_CONTROLS_HUB_CHART_LOWER_AMBER') {
                            row.row![index] = panel.lowerAmber != null ? String(panel.lowerAmber) : null;
                        }

                        if (columnKey === 'CM_CONTROLS_HUB_CHART_LOWER_RED') {
                            row.row![index] = panel.lowerRed != null ? String(panel.lowerRed) : null;
                        }

                        if (columnKey === 'CM_CONTROLS_HUB_CHART_UPPER_AMBER') {
                            row.row![index] = panel.upperAmber != null ? String(panel.upperAmber) : null;
                        }

                        if (columnKey === 'CM_CONTROLS_HUB_CHART_STATUS') {
                            row.row![index] = panel.status ?? null;
                        }

                        if (columnKey === 'CM_CONTROLS_HUB_CHART_OOT_VALUE') {
                            row.row![index] = panel.ootValue != null ? String(panel.ootValue) : null;
                        }

                        if (columnKey === 'CM_CONTROLS_HUB_CHART_Y_AXIS_LABEL') {
                            row.row![index] = panel.yAxisLabel ?? null;
                        }

                        if (columnKey === 'CM_CONTROLS_HUB_CHART_UPPER_RED') {
                            row.row![index] = panel.upperRed != null ? String(panel.upperRed) : null;
                        }

                        if (columnKey === 'CM_CONTROLS_HUB_CHART_TIME_PERIOD') {
                            row.row![index] = stringDateWithoutTimeFromUtc(panel.timePeriod, 'DD-MMM-YYYY') ?? null;
                        }

                        if (columnKey === 'CM_CONTROLS_HUB_CHART_BENCHMARK') {
                            row.row![index] = panel.benchmark != null ? String(panel.benchmark) : null;
                        }
                    });
                });
            });
        });

        return reportData;
    };

    useEffect(() => {
        if (!loadingReport && !isLoading) {
            if (customReportData) {
                const reportData = JSON.parse(JSON.stringify(customReportData));
                const auditLogName = gridActioned.current === 'User' ? t('admin:user') : t('admin:organization');
                addExportFeGridDataAudit({
                    variables: {
                        studyId,
                        filename: `${auditLogName} - ${currentReport?.fullName}`,
                        service: AuditServiceType.CORE,
                        area: AuditApplicationArea.RISK_ASSESSMENT,
                        dataType: AuditDataType.EXCEL_LISTING,
                    },
                })
                    .then((res) => {
                        if (res.data?.addExportFEGridDataAudit?.success) {
                            applyRiskOutOfToleranceOverride(reportData);
                            applyCMControlHubFields(reportData);
                            customReportGenerator(reportData, reportData.generateCustomReport.fileName);
                        } else {
                            NotificationService.showNotification('Something went wrong');
                        }
                    })
                    .catch(() => {
                        NotificationService.showNotification('Something went wrong');
                    });
            }
            gridActioned.current = undefined;
            clearState();
            setCurrentReport(null);
        }
    }, [customReportData, loadingReport]);

    const getCustomReport = (item: ReportDefinitionFields) => {
        loadCustomReport({
            variables: {
                id: item.id,
                studyId,
            },
        }).catch((err) => window.console.error(err));
    };

    useEffect(() => {
        if (!data) return;

        const items = canViewInternalRisks
            ? data.getReportDefinitions
            : data.getReportDefinitions &&
              data.getReportDefinitions
                  .filter((rd) => rd !== null)
                  .filter((item: ReportDefinitionFields) => !item.includeInternalRisks);

        const myItems = items?.filter((item) => item?.userId !== Constants.ObjectId.Empty);
        setMyReports(myItems as ReportDefinitionFields[]);

        const orgItems = items?.filter((item) => item?.userId === Constants.ObjectId.Empty);
        setOrgReports(orgItems as ReportDefinitionFields[]);
    }, [data]);

    useEffect(() => {
        if (currentReport && state.runReport) {
            getCustomReport(currentReport);
        }
    }, [state.runReport]);

    if (!study) return <h1>{t('common:noStudySelected')}</h1>;

    if (!data) return <Loading />;

    if (error) return <ErrorMessage />;

    const myActions: RowMenuAction[] = [
        {
            id: 1,
            label: t('admin:runExcelListing'),
            icon: ['far', 'download'],
            onClick: (item: unknown) => {
                gridActioned.current = 'User';
                setCurrentReport(item as ReportDefinitionFields);
                setState({ runReport: true, runningFromMyReports: true });
            },
        },
        {
            id: 2,
            label: t('admin:editConfig'),
            icon: ['far', 'edit'],
            onClick: (item: unknown) => {
                addExcelListingConfigAction.current = false;
                setCurrentReport(item as ReportDefinitionFields);
                openAddReportConfigDialog.setTrue();
            },
            disabled: !canEditMyListings,
        },
        {
            id: 3,
            label: t('admin:deleteConfig'),
            icon: ['fas', 'trash-xmark'],
            colorProp: 'Red',
            onClick: (item: unknown) => {
                addExcelListingConfigAction.current = false;
                setCurrentReport(item as ReportDefinitionFields);
                openDeleteReportConfigDialog.setTrue();
            },
            disabled: !canEditMyListings,
        },
    ];

    const orgActions: RowMenuAction[] = [
        {
            id: 1,
            label: t('admin:runExcelListing'),
            icon: ['far', 'download'],
            onClick: (item: unknown) => {
                gridActioned.current = 'Organization';
                setCurrentReport(item as ReportDefinitionFields);
                setState({ runReport: true, runningFromMyReports: false });
            },
        },
    ];

    const userExportSuffix = `${user?.firstName}_${user?.lastName}_${study?.organisation?.name}_${moment(new Date()).format('DD-MMM-yyyy')}`;
    const orgExportSuffix = `${study?.organisation?.name}_${moment(new Date()).format('DD-MMM-yyyy')}`;

    return (
        <div ref={gridsWrapRef} className="k-h-full">
            <div className="k-h-1/2">
                <ExcelListingsGrid
                    reports={myReports}
                    actions={myActions}
                    title={t('admin:myExcelListings')}
                    showAdd
                    addCallback={() => {
                        openAddReportConfigDialog.setTrue();
                        addExcelListingConfigAction.current = true;
                    }}
                    downloadingReport={gridActioned.current === 'User' ? loadingReport : false}
                    exportFileName={`My_Excel_Listings_${userExportSuffix}`}
                    canEdit={canEditMyListings}
                />
                {openAddReportConfigDialog.value && (
                    <AddEditExcelListingsConfigDialog
                        addExcelListingsConfigAction={addExcelListingConfigAction.current}
                        reportDefinitions={myReports}
                        reportDefinition={currentReport ?? undefined}
                        isUserReport
                        canViewInternalRisks={canViewInternalRisks ?? false}
                        onClose={() => {
                            openAddReportConfigDialog.setFalse();
                            setCurrentReport(null);
                        }}
                    />
                )}
                {openDeleteReportConfigDialog.value && (
                    <DeleteExcelListingsConfigDialog
                        onClose={() => {
                            openDeleteReportConfigDialog.setFalse();
                            setCurrentReport(null);
                        }}
                        reportDefinition={currentReport ?? undefined}
                    />
                )}
            </div>
            <div className="k-h-1/2">
                <ExcelListingsGrid
                    reports={orgReports}
                    actions={orgActions}
                    title={t('admin:organizationExcelListings')}
                    showAdd={false}
                    downloadingReport={gridActioned.current === 'Organization' ? loadingReport : false}
                    exportFileName={`Organization_Excel_Listings_${orgExportSuffix}`}
                />
            </div>
        </div>
    );
};

export default RAMExcelListing;
